import type { ParsedMailFile } from "../../services/mailFileService";
const normalize = (cid: string) => {
    try { return decodeURIComponent(cid).trim().replace(/^<|>$/g, ""); }
    catch { return cid.trim().replace(/^<|>$/g, ""); }
};
const matchesCid = (value: string, cid: string) => /^cid:/i.test(value.trim()) && normalize(value.trim().slice(4)) === cid;
const replaceCssCid = (value: string, cid: string, url: string) => value.replace(
    /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi,
    (original, _quote, reference: string) => matchesCid(reference, cid) ? `url("${url}")` : original,
);
/** T-236: Anker eines Bilds (`data-kmvs-cid`) — Zeichenvorrat wie `cidMarkerAttribut`, sonst keiner. */
const markerWert = (cid: string) => { const key = cid.toLowerCase(); return /^[a-z0-9._@+$-]+$/.test(key) ? key : null; };
type ImageReference = { get: () => string; set: (value: string) => void; css: boolean; bild?: Element };
/** Nur passive Bild-URLs austauschen, niemals MIME-Bytes oder Anhangindizes ändern. */
export async function resolveArchivedInlineParts(html: string, parts: NonNullable<ParsedMailFile["inlineParts"]>, fetchBlob: (index: number) => Promise<Blob>) {
    const template = document.createElement("template"); template.innerHTML = html;
    const urls: string[] = [];
    const warnings: string[] = [];
    const references: ImageReference[] = [];
    const attributes = (selector: string, attribute: string, css: boolean) => {
        for (const element of Array.from(template.content.querySelectorAll(selector))) references.push({
            get: () => element.getAttribute(attribute) ?? "", set: value => element.setAttribute(attribute, value), css,
            bild: element.tagName === "IMG" && attribute === "src" ? element : undefined,
        });
    };
    attributes("img[src]", "src", false);
    attributes("table[background],td[background],th[background],body[background]", "background", false);
    attributes("[style]", "style", true);
    for (const element of Array.from(template.content.querySelectorAll("style"))) references.push({
        get: () => element.textContent ?? "", set: value => { element.textContent = value; }, css: true,
    });
    const counts = new Map<string, number>();
    for (const part of parts) counts.set(normalize(part.contentId), (counts.get(normalize(part.contentId)) ?? 0) + 1);
    await Promise.all(parts.map(async part => {
        const cid = normalize(part.contentId);
        if (counts.get(cid) !== 1 || !/^image\/(png|jpeg|gif|webp|bmp|x-icon|vnd.microsoft.icon)$/i.test(part.contentType)) return;
        const matching = references.filter(ref => ref.css
            ? replaceCssCid(ref.get(), cid, "blob:probe") !== ref.get()
            : matchesCid(ref.get(), cid));
        if (!matching.length) return;
        try {
            const blob = await fetchBlob(part.index);
            const url = URL.createObjectURL(new Blob([blob], { type: part.contentType })); urls.push(url);
            // Aktuellen Wert lesen: zwei gleichzeitig geladene CIDs derselben
            // CSS-Deklaration dürfen sich nicht gegenseitig überschreiben.
            for (const ref of matching) ref.set(ref.css ? replaceCssCid(ref.get(), cid, url) : url);
            // T-236: Klick aufs Bild findet über den Marker seinen Anhang.
            const marker = markerWert(cid);
            if (marker) for (const ref of matching) ref.bild?.setAttribute("data-kmvs-cid", marker);
        } catch { warnings.push("Ein eingebettetes Bild konnte nicht geladen werden."); }
    }));
    return { html: template.innerHTML, warnings, dispose: () => urls.forEach(url => URL.revokeObjectURL(url)) };
}
